<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { SCToggle, SCIcon, SCListItemRow, SCRadioButton } from '@nsftx/seven-components'
import STWalletBalanceDetailsHeader from './STWalletBalanceDetailsHeader.vue'
import STWalletBalanceVisibilityToggle from './STWalletBalanceVisibilityToggle.vue'
import { useWalletStore } from '@/stores/wallet/walletStore'
import STWalletBalanceDetailsMultipleItem from './STWalletBalanceDetailsMultipleItem.vue'
import { useWalletBalanceStore } from '@/stores/wallet/walletBalanceStore'
import { formatNumber, getCurrencySymbol } from '@nsftx/seven-nxg-retail-vue-sdk'

const { activeWallet, wallets } = storeToRefs(useWalletStore())
const { setActiveWallet } = useWalletStore()
const walletBalanceStore = useWalletBalanceStore()
const { showWalletBalanceMultipleItemModal } = walletBalanceStore
const { isWalletBalanceMultipleItemModalOpened, isWalletBalanceVisible } =
  storeToRefs(walletBalanceStore)

const toggle = ref<boolean>(false)
const selectedWalletId = ref<string>(activeWallet.value?.id || '')

const selectedWalletFormattedBalance = computed(() => {
  const wallet = wallets.value?.find((wallet) => wallet.id === selectedWalletId.value)

  if (!wallet) {
    return ''
  }

  if (isWalletBalanceVisible.value) {
    return formatNumber(wallet?.balance || '0', {
      style: 'currency',
      currencySymbol: getCurrencySymbol(wallet?.currency || '')
    })
  }

  return `* * * * ${getCurrencySymbol(wallet?.currency || '')}`
})

watch(selectedWalletId, setActiveWallet)

const handleWalletClick = (walletId: string) => {
  selectedWalletId.value = walletId
  showWalletBalanceMultipleItemModal()
}

const onWalletSelected = (value: string) => {
  selectedWalletId.value = value
}
</script>

<template>
  <div class="st-wallet-balance-details__multiple">
    <div v-if="!isWalletBalanceMultipleItemModalOpened" class="st-d-flex-column">
      <STWalletBalanceDetailsHeader>
        <template #icon-left>
          <SCIcon icon="n-i-bills" size="2xl" color="var(--sc-main-primary)" />
        </template>
        <template #title>{{ $t('common_total_balance') }}</template>
        <template #value>{{ selectedWalletFormattedBalance }}</template>
        <template #icon-right>
          <STWalletBalanceVisibilityToggle />
        </template>
      </STWalletBalanceDetailsHeader>
      <div class="st-d-flex-column sc-bg-400">
        <div class="st-d-flex sc-p-7">
          <div class="st-d-flex-column">
            <h4 class="sc-text-button sc-text-primary-1 sc-mb-4">
              {{ $t('wallet_multiple_primary_wallet_title') }}
            </h4>
            <p class="sc-text-caption-2 sc-text-primary-2 sc-pr-9">
              {{ $t('wallet_multiple_primary_wallet_text') }}
            </p>
          </div>
          <div class="st-h-100 sc-mt-3">
            <SCToggle v-model="toggle" />
          </div>
        </div>
        <div
          v-for="wallet in wallets"
          :key="wallet.id"
          class="sc-px-3 sc-mb-3 st-pointer"
          data-testid="listItemWallet"
          @click="handleWalletClick(wallet.id)"
        >
          <SCListItemRow large background="var(--sc-theme-300)">
            <div class="st-flex-center-between st-w-100">
              <div class="st-flex-center">
                <SCRadioButton
                  :id="wallet.id"
                  :value="wallet.id"
                  :selected-value="selectedWalletId"
                  class="sc-mr-6"
                  large
                  @click.stop
                  @update:model-value="onWalletSelected"
                />
                <span class="sc-text-button sc-text-primary-1 sc-pr-3">{{ wallet.currency }}</span>
                |
                <span class="sc-text-button sc-text-primary-1 sc-pl-3">{{
                  formatNumber(wallet.balance, {
                    style: 'currency',
                    currencySymbol: getCurrencySymbol(wallet.currency)
                  })
                }}</span>
              </div>
              <SCIcon icon="n-i-arrow-right-d" size="lg" class="" />
            </div>
          </SCListItemRow>
        </div>
      </div>
    </div>
    <STWalletBalanceDetailsMultipleItem v-else :wallet-id="selectedWalletId" />
  </div>
</template>
